/**
 * Words somebody typed and has not sent yet, kept across the card they were
 * typed in.
 *
 * A pending draft is retired the moment the client writes again: the pipeline
 * marks it stale and composes a new one, the screen refetches, and the card the
 * manager was typing in unmounts. Their half-written correction went with it --
 * silently, into an identical-looking card holding the model's fresh wording.
 * The likeliest outcome was approving that wording believing the correction was
 * still in it.
 *
 * So the text outlives the component. It is not put into the new draft by
 * itself: the new draft answers a message the old one had not seen, and quietly
 * swapping one for the other is the same mistake pointing the other way. The
 * next card offers it back, in their own words, and they decide.
 *
 * In memory only, keyed by conversation. Nothing here is a draft of record --
 * that is the server's job -- and a reload is allowed to lose it.
 */

export interface KeptEdit {
  /** The draft it was typed into. */
  readonly draftID: string
  readonly text: string
}

const kept = new Map<string, KeptEdit>()

/**
 * Remembers what is in the editor.
 *
 * Deliberately silent: this runs on every keystroke, and waking the whole
 * conversation up for each one would make typing feel like the network. What
 * reads it is the next card's first render, which happens after the swap.
 */
export function keepEdit(threadID: string, draftID: string, text: string): void {
  kept.set(threadID, { draftID, text })
}

/** Forgets it: sent, rejected, taken back, or offered and declined. */
export function forgetEdit(threadID: string): void {
  kept.delete(threadID)
}

/**
 * What was left over from another draft in this conversation, if anything.
 *
 * An edit belonging to the draft asking for it is not "left over" -- that is the
 * card's own state, and it already has it.
 */
export function editLeftOver(threadID: string, draftID: string): KeptEdit | undefined {
  const edit = kept.get(threadID)
  if (edit === undefined || edit.draftID === draftID) return undefined
  if (edit.text.trim() === '') return undefined
  return edit
}
